const vertexShaderSource = `
precision mediump float;

attribute vec2 a_position;
uniform vec2 u_resolution;
varying vec2 v_position;

void main() {
    vec2 clipSpace = ((a_position / u_resolution) * 2.0) - 1.0;
    gl_Position = vec4(clipSpace * vec2(1, -1), 0, 1);
    v_position = a_position;
}
`;

const fragmentShaderSource = `
precision mediump float;

uniform vec2 u_resolution;
uniform vec2 u_center;
uniform float u_outerRadius;
uniform float u_innerRadius;
uniform float u_time;
uniform int u_hoveredSection;
uniform int u_pressedSection;

varying vec2 v_position;

float circle(vec2 pos, float radius) {
    return 1.0 - smoothstep(radius - 2.0, radius, length(pos));
}

float ring(vec2 pos, float innerR, float outerR) {
    float outer = circle(pos, outerR);
    float inner = 1.0 - circle(pos, innerR);
    return outer * inner;
}

int getSection(vec2 pos) {
    float angle = atan(pos.y, pos.x);
    angle = angle + 3.14159 * 2.0;
    angle = mod(angle, 3.14159 * 2.0);
    
    if (angle >= 7.0 * 3.14159 / 4.0 || angle < 3.14159 / 4.0) return 1; // right
    else if (angle >= 3.14159 / 4.0 && angle < 3.0 * 3.14159 / 4.0) return 2; // down
    else if (angle >= 3.0 * 3.14159 / 4.0 && angle < 5.0 * 3.14159 / 4.0) return 3; // left
    else return 0; // up
}

vec3 drawArrow(vec2 pos, float angle, float distance, bool highlight) {
    vec2 rotated = vec2(
        pos.x * cos(-angle) - pos.y * sin(-angle),
        pos.x * sin(-angle) + pos.y * cos(-angle)
    );
    
    vec2 arrowPos = rotated - vec2(0.0, distance);
    float arrowSize = 8.0;
    
    // Arrow shape
    float arrow = 0.0;
    if (arrowPos.y < -arrowSize && arrowPos.y > -arrowSize - 8.0) {
        float width = (arrowSize + arrowPos.y + 8.0) * 0.5;
        if (abs(arrowPos.x) < width) {
            arrow = 1.0;
        }
    }
    
    float intensity = highlight ? 0.9 : 0.6;
    return vec3(intensity) * arrow;
}

void main() {
    vec2 pos = v_position - u_center;
    float dist = length(pos);
    
    vec3 color = vec3(0.1); // Dark background
    
    // Outer ring
    if (dist < u_outerRadius && dist > u_innerRadius + 10.0) {
        float ringMask = ring(pos, u_innerRadius + 10.0, u_outerRadius - 15.0);
        
        int section = getSection(pos);
        bool isHovered = (u_hoveredSection == section);
        bool isPressed = (u_pressedSection == section);
        
        float baseIntensity = 0.2;
        if (isPressed) baseIntensity = 0.35;
        else if (isHovered) baseIntensity = 0.28;
        
        color = mix(color, vec3(baseIntensity), ringMask);
        
        // Add metallic gradient
        float gradient = (dist - u_innerRadius) / (u_outerRadius - u_innerRadius);
        gradient = smoothstep(0.0, 1.0, gradient);
        color += vec3(0.05) * (1.0 - gradient) * ringMask;
    }
    
    // Center circle
    if (dist < u_innerRadius) {
        bool centerHovered = (u_hoveredSection == 4);
        bool centerPressed = (u_pressedSection == 4);
        
        float centerIntensity = 0.22;
        if (centerPressed) centerIntensity = 0.35;
        else if (centerHovered) centerIntensity = 0.28;
        
        float centerMask = circle(pos, u_innerRadius - 3.0);
        color = mix(color, vec3(centerIntensity), centerMask);
        
        // Center highlight
        if (centerHovered || centerPressed) {
            float innerHighlight = circle(pos, u_innerRadius - 12.0);
            color += vec3(0.05) * innerHighlight;
        }
    }
    
    // Draw arrows
    float arrowDistance = (u_outerRadius + u_innerRadius) / 2.0 - 5.0;
    
    // Up arrow
    bool upHighlight = (u_hoveredSection == 0) || (u_pressedSection == 0);
    color += drawArrow(pos, -1.5708, arrowDistance, upHighlight);
    
    // Right arrow
    bool rightHighlight = (u_hoveredSection == 1) || (u_pressedSection == 1);
    color += drawArrow(pos, 0.0, arrowDistance, rightHighlight);
    
    // Down arrow
    bool downHighlight = (u_hoveredSection == 2) || (u_pressedSection == 2);
    color += drawArrow(pos, 1.5708, arrowDistance, downHighlight);
    
    // Left arrow
    bool leftHighlight = (u_hoveredSection == 3) || (u_pressedSection == 3);
    color += drawArrow(pos, 3.14159, arrowDistance, leftHighlight);
    
    // Outer border
    float outerBorder = 1.0 - smoothstep(u_outerRadius - 2.0, u_outerRadius, dist);
    float innerBorder = smoothstep(u_outerRadius - 6.0, u_outerRadius - 4.0, dist);
    float border = outerBorder * innerBorder;
    color = mix(color, vec3(0.05), border);
    
    // Inner circle border
    float innerCircleBorder = abs(dist - u_innerRadius);
    innerCircleBorder = 1.0 - smoothstep(1.0, 3.0, innerCircleBorder);
    color = mix(color, vec3(0.05), innerCircleBorder * 0.5);
    
    gl_FragColor = vec4(color, 1.0);
}
`;

import { EventEmitter } from 'eventemitter3';

type SectionName = 'up' | 'right' | 'down' | 'left' | 'center' | null;

export class WebGLCircularControl extends EventEmitter {
    private canvas: HTMLCanvasElement;
    private gl: WebGLRenderingContext | null = null;
    private program: WebGLProgram | null = null;
    private hoveredSection: SectionName = null;
    private pressedSection: SectionName = null;

    constructor() {
        super();
        this.canvas = document.getElementById('canvas') as HTMLCanvasElement;

        this.init();
        this.setupEventListeners();
        this.render();
    }

    private createShader(type: number, source: string): WebGLShader | null {
        if (!this.gl) return null;
        const shader = this.gl.createShader(type);
        if (!shader) return null;
        this.gl.shaderSource(shader, source);
        this.gl.compileShader(shader);
        
        if (!this.gl.getShaderParameter(shader, this.gl.COMPILE_STATUS)) {
            console.error('Shader compilation error:', this.gl.getShaderInfoLog(shader));
            this.gl.deleteShader(shader);
            return null;
        }
        return shader;
    }

    private createProgram(vertexShader: WebGLShader, fragmentShader: WebGLShader): WebGLProgram | null {
        if (!this.gl) return null;
        const program = this.gl.createProgram();
        if (!program) return null;
        this.gl.attachShader(program, vertexShader);
        this.gl.attachShader(program, fragmentShader);
        this.gl.linkProgram(program);
        
        if (!this.gl.getProgramParameter(program, this.gl.LINK_STATUS)) {
            console.error('Program linking error:', this.gl.getProgramInfoLog(program));
            this.gl.deleteProgram(program);
            return null;
        }
        return program;
    }

    private init(): void {
        this.gl = this.canvas.getContext('webgl');
        
        if (!this.gl) {
            console.error('WebGL not supported');
            return;
        }
        
        const vertexShader = this.createShader(this.gl.VERTEX_SHADER, vertexShaderSource);
        const fragmentShader = this.createShader(this.gl.FRAGMENT_SHADER, fragmentShaderSource);
        if (!vertexShader || !fragmentShader) return;
        
        this.program = this.createProgram(vertexShader, fragmentShader);
        if (!this.program) return;
        
        const vertices = new Float32Array([
            0, 0,
            this.canvas.width, 0,
            0, this.canvas.height,
            0, this.canvas.height,
            this.canvas.width, 0,
            this.canvas.width, this.canvas.height
        ]);
        
        const buffer = this.gl.createBuffer();
        if (!buffer) return;
        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, buffer);
        this.gl.bufferData(this.gl.ARRAY_BUFFER, vertices, this.gl.STATIC_DRAW);
        
        const positionLocation = this.gl.getAttribLocation(this.program, 'a_position');
        this.gl.enableVertexAttribArray(positionLocation);
        this.gl.vertexAttribPointer(positionLocation, 2, this.gl.FLOAT, false, 0, 0);
        
        this.gl.useProgram(this.program);
    }

    private getSection(x: number, y: number): SectionName {
        const centerX = this.canvas.width / 2;
        const centerY = this.canvas.height / 2;
        const dx = x - centerX;
        const dy = y - centerY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const outerRadius = Math.min(this.canvas.width, this.canvas.height) / 2 - 10;
        const innerRadius = outerRadius * 0.35;
        
        if (distance <= innerRadius) {
            return 'center';
        }
        
        if (distance > outerRadius - 15) {
            return null;
        }
        
        const angle = Math.atan2(dy, dx);
        const normalizedAngle = ((angle + Math.PI * 2) % (Math.PI * 2));
        
        if (normalizedAngle >= Math.PI * 7/4 || normalizedAngle < Math.PI / 4) {
            return 'right';
        } else if (normalizedAngle >= Math.PI / 4 && normalizedAngle < Math.PI * 3/4) {
            return 'down';
        } else if (normalizedAngle >= Math.PI * 3/4 && normalizedAngle < Math.PI * 5/4) {
            return 'left';
        } else {
            return 'up';
        }
    }

    private setupEventListeners(): void {
        this.canvas.addEventListener('mousemove', (e: MouseEvent) => {
            const rect = this.canvas.getBoundingClientRect();
            const x = (e.clientX - rect.left) * (this.canvas.width / rect.width);
            const y = (e.clientY - rect.top) * (this.canvas.height / rect.height);
            
            const section = this.getSection(x, y);
            if (section !== this.hoveredSection) {
                this.hoveredSection = section;
                this.render();
                this.updateStatus();
            }
        });

        this.canvas.addEventListener('mousedown', (e: MouseEvent) => {
            const rect = this.canvas.getBoundingClientRect();
            const x = (e.clientX - rect.left) * (this.canvas.width / rect.width);
            const y = (e.clientY - rect.top) * (this.canvas.height / rect.height);
            
            const section = this.getSection(x, y);
            if (section !== this.pressedSection) {
                this.pressedSection = section;
                this.render();
                this.updateStatus();
                
                // Emit button press event for network communication
                if (section) {
                    this.emitButtonPress(section);
                }
            }
        });

        this.canvas.addEventListener('mouseup', () => {
            if (this.pressedSection !== null) {
                this.pressedSection = null;
                this.render();
                this.updateStatus();
            }
        });

        this.canvas.addEventListener('mouseleave', () => {
            if (this.hoveredSection !== null || this.pressedSection !== null) {
                this.hoveredSection = null;
                this.pressedSection = null;
                this.render();
                this.updateStatus();
            }
        });
    }

    private updateStatus(): void {
        
    }

    private emitButtonPress(section: SectionName): void {
        if (!section) return;
        
        const buttonMap: Record<string, number> = {
            'up': 1,
            'right': 4, 
            'down': 2,
            'left': 3,
            'center': 5
        };

        const buttonValue = buttonMap[section];
        if (buttonValue) {
            this.emit('buttonPress', {
                button: buttonValue,
                section: section
            });
        }
    }

    private render(): void {
        if (!this.gl || !this.program) return;
        
        this.gl.viewport(0, 0, this.canvas.width, this.canvas.height);
        this.gl.clearColor(0.1, 0.1, 0.1, 1.0);
        this.gl.clear(this.gl.COLOR_BUFFER_BIT);
        
        const resolutionLocation = this.gl.getUniformLocation(this.program, 'u_resolution');
        this.gl.uniform2f(resolutionLocation, this.canvas.width, this.canvas.height);
        
        const centerLocation = this.gl.getUniformLocation(this.program, 'u_center');
        this.gl.uniform2f(centerLocation, this.canvas.width / 2, this.canvas.height / 2);
        
        const outerRadiusLocation = this.gl.getUniformLocation(this.program, 'u_outerRadius');
        this.gl.uniform1f(outerRadiusLocation, Math.min(this.canvas.width, this.canvas.height) / 2 - 10);
        
        const innerRadiusLocation = this.gl.getUniformLocation(this.program, 'u_innerRadius');
        this.gl.uniform1f(innerRadiusLocation, (Math.min(this.canvas.width, this.canvas.height) / 2 - 10) * 0.35);
        
        const timeLocation = this.gl.getUniformLocation(this.program, 'u_time');
        this.gl.uniform1f(timeLocation, Date.now() / 1000);
        
        const sectionMap: Record<Exclude<SectionName, null>, number> = {
            'up': 0,
            'right': 1,
            'down': 2,
            'left': 3,
            'center': 4
        };
        
        const hoveredSectionLocation = this.gl.getUniformLocation(this.program, 'u_hoveredSection');
        this.gl.uniform1i(hoveredSectionLocation, this.hoveredSection ? sectionMap[this.hoveredSection] : -1);
        
        const pressedSectionLocation = this.gl.getUniformLocation(this.program, 'u_pressedSection');
        this.gl.uniform1i(pressedSectionLocation, this.pressedSection ? sectionMap[this.pressedSection] : -1);
        
        this.gl.drawArrays(this.gl.TRIANGLES, 0, 6);
    }
}